import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ControlBadge } from "@/components/ControlBadge";
import {
  Loader2, Save, ArrowLeft, Lock, MapPin, Eye, EyeOff, Archive, RotateCcw, CheckCircle2, X, Plus, FileEdit,
} from "lucide-react";

const NAVY = "#1C4358";
const ic = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";

const STATUS_LABEL = { published: "Published", unpublished: "Unpublished", draft: "Draft", archived: "Archived" };

const GROUPS = [
  { key: "identity", title: "Identity & structure" },
  { key: "contact", title: "Local contact" },
  { key: "locale", title: "Locale & availability" },
  { key: "team", title: "Local team" },
  { key: "seo", title: "Local SEO" },
];

const FieldShell = ({ fdef, children }) => (
  <div className="space-y-1.5" data-testid={`field-${fdef.field}`}>
    <Label className="text-[13px] font-semibold text-[#1C4358] flex items-center gap-2 flex-wrap">
      {fdef.label}
      <ControlBadge kind={fdef.kind} />
      {!fdef.editable && fdef.kind === "hybrid" && (
        <span className="inline-flex items-center gap-1 text-[11px] text-[#B7791F]" data-testid={`hybrid-note-${fdef.field}`}>
          <Lock size={11} /> {fdef.approval_note || "Approval required (14F)"}
        </span>
      )}
      {!fdef.editable && fdef.kind !== "hybrid" && (
        <span className="inline-flex items-center gap-1 text-[11px] text-[#7A8A99]"><Lock size={11} /> Locked</span>
      )}
    </Label>
    {children}
  </div>
);

export default function FranchiseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyStatus, setBusyStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        api.get("/franchise-admin/meta"),
        api.get(`/franchise-admin/franchises/${id}`),
      ]);
      setMeta(m.data);
      setData(d.data);
      const f = d.data.franchise;
      const init = {};
      (d.data.field_matrix || []).forEach((fd) => {
        const v = f[fd.field];
        init[fd.field] = v == null ? (["users", "lanes"].includes(fd.type) ? [] : "") : v;
      });
      setForm(init);
    } catch (e) {
      toast.error(errMsg(e, "Could not load this franchise."));
      if (e?.response?.status === 403 || e?.response?.status === 404) navigate("/franchise-admin");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const editable = (data.field_matrix || []).filter((fd) => fd.editable);
    if (editable.length === 0) { toast.info("You don't have editable fields on this franchise."); return; }
    const payload = {};
    editable.forEach((fd) => { payload[fd.field] = form[fd.field]; });
    setSaving(true);
    try {
      const { data: res } = await api.put(`/franchise-admin/franchises/${id}`, payload);
      toast.success("Saved.");
      setData((d) => ({ ...d, franchise: res.franchise }));
    } catch (e) { toast.error(errMsg(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  const changeStatus = async (action) => {
    setBusyStatus(true);
    try {
      const { data: res } = await api.post(`/franchise-admin/franchises/${id}/${action}`);
      setData((d) => ({ ...d, franchise: res.franchise }));
      toast.success(`Sub-site ${action}ed.`);
    } catch (e) { toast.error(errMsg(e, "Status change failed.")); }
    finally { setBusyStatus(false); }
  };

  if (loading || !data || !meta) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  const fr = data.franchise;
  const matrix = data.field_matrix || [];
  const staff = meta.staff || [];
  const staffName = (uid) => (staff.find((s) => s.id === uid)?.name) || uid;

  const renderInput = (fd) => {
    const val = form[fd.field];
    const dis = !fd.editable;
    if (fd.type === "textarea") {
      return <Textarea rows={2} className={ic} disabled={dis} data-testid={`input-${fd.field}`}
        value={val || ""} onChange={(e) => setF(fd.field, e.target.value)} />;
    }
    if (fd.type === "timezone" || fd.type === "currency" || fd.type === "language") {
      const opts = fd.type === "timezone" ? (meta.timezones || []).map((t) => ({ v: t, l: t }))
        : (fd.type === "currency" ? (meta.currencies || []) : (meta.languages || [])).map((o) => ({ v: o.code, l: o.label }));
      return (
        <Select value={val || "__none__"} disabled={dis} onValueChange={(v) => setF(fd.field, v === "__none__" ? "" : v)}>
          <SelectTrigger className={ic} data-testid={`input-${fd.field}`}><SelectValue placeholder="Not set" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Not set</SelectItem>
            {opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (fd.type === "user") {
      return (
        <Select value={val || "__none__"} disabled={dis} onValueChange={(v) => setF(fd.field, v === "__none__" ? "" : v)}>
          <SelectTrigger className={ic} data-testid={`input-${fd.field}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Unassigned</SelectItem>
            {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.role_label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (fd.type === "users") {
      const arr = Array.isArray(val) ? val : [];
      const avail = staff.filter((s) => !arr.includes(s.id));
      return (
        <div className="space-y-2" data-testid={`input-${fd.field}`}>
          <div className="flex flex-wrap gap-1.5">
            {arr.length === 0 && <span className="text-[12px] text-[#7A8A99]">None assigned</span>}
            {arr.map((uid) => (
              <span key={uid} className="inline-flex items-center gap-1 text-[12px] bg-[#1C4358]/8 text-[#1C4358] rounded-full px-2 py-0.5">
                {staffName(uid)}
                {!dis && <button data-testid={`remove-${fd.field}-${uid}`} onClick={() => setF(fd.field, arr.filter((x) => x !== uid))}><X size={12} /></button>}
              </span>
            ))}
          </div>
          {!dis && (
            <Select value="__add__" onValueChange={(v) => { if (v !== "__add__") setF(fd.field, [...arr, v]); }}>
              <SelectTrigger className={`${ic} h-9`} data-testid={`add-${fd.field}`}><span className="inline-flex items-center gap-1 text-[13px]"><Plus size={13} /> Add person</span></SelectTrigger>
              <SelectContent>
                <SelectItem value="__add__" disabled>Add person…</SelectItem>
                {avail.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.role_label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      );
    }
    if (fd.type === "lanes") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="flex flex-wrap gap-1.5" data-testid={`input-${fd.field}`}>
          {(meta.lanes || []).map((l) => {
            const on = arr.includes(l.slug);
            return (
              <button key={l.slug} disabled={dis} data-testid={`lane-toggle-${l.slug}`}
                onClick={() => setF(fd.field, on ? arr.filter((x) => x !== l.slug) : [...arr, l.slug])}
                className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${on
                  ? "bg-[#2F7D5A] text-white border-[#2F7D5A]" : "bg-white text-[#4B5563] border-[#D6E0E7]"} ${dis ? "opacity-60" : "hover:border-[#2F7D5A]"}`}>
                {l.name}
              </button>
            );
          })}
        </div>
      );
    }
    return <Input type={fd.type === "email" ? "email" : "text"} className={ic} disabled={dis}
      data-testid={`input-${fd.field}`} value={val || ""} onChange={(e) => setF(fd.field, e.target.value)} />;
  };

  const anyEditable = matrix.some((fd) => fd.editable);

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="franchise-detail-page">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate("/franchise-admin")} data-testid="back-btn" className="text-[#1C4358] hover:text-[#9C1E22]"><ArrowLeft size={20} /></button>
          <MapPin size={18} style={{ color: NAVY }} />
          <h1 className="text-lg font-display font-bold text-[#1C4358] truncate">{fr.name}</h1>
          <Badge variant="outline" data-testid="detail-status" className="text-[11px]">{STATUS_LABEL[fr.status] || fr.status}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/franchise-admin/${id}/content`)} data-testid="manage-content-btn" className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
            <FileEdit size={15} /> Local content <ControlBadge kind="hybrid" />
          </Button>
          {anyEditable && (
            <Button onClick={save} disabled={saving} data-testid="franchise-save-btn" className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </Button>
          )}
        </div>
      </div>

      <p className="mt-3 text-[13px] text-[#4B5563]">
        {[fr.territory_name, fr.city_name, fr.state_name, fr.country_name].filter(Boolean).join(" · ")}
        {fr.local_path && <span className="font-mono ml-2">{fr.local_path}</span>}
      </p>

      {/* Status actions */}
      {data.can_manage_status && (
        <Card className="mt-4 p-4 border-[#E6EDF2]" data-testid="status-actions">
          <div className="flex items-center gap-2 mb-2 text-[13px] font-bold text-[#1C4358]">Publishing <ControlBadge kind="local" /></div>
          <div className="flex flex-wrap gap-2">
            {fr.status !== "published" && fr.status !== "archived" && (
              <Button size="sm" data-testid="btn-publish" disabled={busyStatus} onClick={() => changeStatus("publish")} className="gap-1 bg-[#2F7D5A] hover:bg-[#25634699] text-white"><Eye size={14} /> Publish</Button>
            )}
            {fr.status === "published" && (
              <Button size="sm" variant="outline" data-testid="btn-unpublish" disabled={busyStatus} onClick={() => changeStatus("unpublish")} className="gap-1 border-[#D6E0E7] text-[#B7791F]"><EyeOff size={14} /> Unpublish</Button>
            )}
            {fr.status !== "archived" && (
              <Button size="sm" variant="outline" data-testid="btn-archive" disabled={busyStatus} onClick={() => changeStatus("archive")} className="gap-1 border-[#D6E0E7] text-[#9C1E22]"><Archive size={14} /> Archive</Button>
            )}
            {fr.status === "archived" && (
              <Button size="sm" data-testid="btn-restore" disabled={busyStatus} onClick={() => changeStatus("restore")} className="gap-1 bg-[#1C4358] hover:bg-[#163646] text-white"><RotateCcw size={14} /> Restore</Button>
            )}
          </div>
        </Card>
      )}

      {/* Field groups */}
      <Accordion type="multiple" defaultValue={["contact", "locale", "team"]} className="mt-4 space-y-3">
        {GROUPS.map((g) => {
          const fields = matrix.filter((fd) => fd.group === g.key);
          if (fields.length === 0) return null;
          return (
            <AccordionItem key={g.key} value={g.key} className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
              <AccordionTrigger className="font-display font-semibold text-[#1C4358]">{g.title}</AccordionTrigger>
              <AccordionContent className="space-y-4 pb-5">
                {g.key === "seo" && !fields[0].editable && (
                  <div className="flex items-center gap-2 rounded-lg bg-[rgba(183,121,31,0.10)] px-3 py-2 text-[12px] text-[#B7791F]">
                    <Lock size={13} /> Hybrid content — local drafts will go through Super Admin approval (workflow arrives in 14F). Read-only for your role.
                  </div>
                )}
                {fields.map((fd) => <FieldShell key={fd.field} fdef={fd}>{renderInput(fd)}</FieldShell>)}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Service areas */}
      <ServiceAreasPanel areas={data.service_areas || []} canEdit={data.can_edit_service_area} onChanged={load} />
    </div>
  );
}

function ServiceAreasPanel({ areas, canEdit, onChanged }) {
  return (
    <div className="mt-6" data-testid="service-areas-panel">
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={16} className="text-[#1C4358]" />
        <h2 className="text-[15px] font-display font-semibold text-[#1C4358]">Mapped service areas</h2>
        <ControlBadge kind="local" />
      </div>
      {areas.length === 0 && <p className="text-[13px] text-[#7A8A99]">No service areas linked to this territory.</p>}
      <div className="space-y-3">
        {areas.map((sa) => <ServiceAreaCard key={sa.id} sa={sa} canEdit={canEdit} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function ServiceAreaCard({ sa, canEdit, onChanged }) {
  const [zips, setZips] = useState((sa.zip_codes || []).join(", "));
  const [mapped, setMapped] = useState(sa.mapped_area || "");
  const [notes, setNotes] = useState(sa.notes || "");
  const [geo, setGeo] = useState(sa.geojson ? JSON.stringify(sa.geojson, null, 2) : "");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    let geojson = null;
    if (geo.trim()) {
      try { geojson = JSON.parse(geo); } catch { toast.error("GeoJSON is not valid JSON."); return; }
    }
    setSaving(true);
    try {
      await api.put(`/franchise-admin/service-areas/${sa.id}`, {
        zip_codes: zips.split(",").map((z) => z.trim()).filter(Boolean),
        mapped_area: mapped, notes, geojson,
      });
      toast.success("Service area saved.");
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  const doAction = async (action) => {
    setBusy(true);
    try {
      await api.post(`/franchise-admin/service-areas/${sa.id}/${action}`);
      toast.success(`Service area ${action}d.`);
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Action failed.")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border-[#E6EDF2] space-y-3" data-testid={`service-area-${sa.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display font-semibold text-[#1C4358] text-[15px]">{sa.name}</span>
        <div className="flex items-center gap-2">
          {sa.status === "archived"
            ? <Badge className="bg-[#9C1E22]/10 text-[#9C1E22] text-[10px]" variant="outline">Archived</Badge>
            : <Badge className="bg-[#2F7D5A]/10 text-[#2F7D5A] text-[10px]" variant="outline"><CheckCircle2 size={11} className="mr-1" /> Live</Badge>}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-[#1C4358]">Zip / postal codes (comma-separated)</Label>
        <Input className={ic} disabled={!canEdit} data-testid={`sa-zips-${sa.id}`} value={zips} onChange={(e) => setZips(e.target.value)} placeholder="64105, 64106" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-[#1C4358]">Mapped area (text fallback)</Label>
        <Textarea rows={2} className={ic} disabled={!canEdit} data-testid={`sa-mapped-${sa.id}`} value={mapped} onChange={(e) => setMapped(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-[#1C4358]">Area notes</Label>
        <Input className={ic} disabled={!canEdit} data-testid={`sa-notes-${sa.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-[#1C4358]">GeoJSON polygon (optional — not required to publish)</Label>
        <Textarea rows={3} className={`${ic} font-mono text-[12px]`} disabled={!canEdit} data-testid={`sa-geojson-${sa.id}`}
          value={geo} onChange={(e) => setGeo(e.target.value)} placeholder='{"type":"Polygon","coordinates":[...]}' />
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid={`sa-save-${sa.id}`} className="gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save area
          </Button>
          {sa.status === "archived"
            ? <Button size="sm" variant="outline" disabled={busy} data-testid={`sa-restore-${sa.id}`} onClick={() => doAction("restore")} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><RotateCcw size={13} /> Restore</Button>
            : <Button size="sm" variant="outline" disabled={busy} data-testid={`sa-archive-${sa.id}`} onClick={() => doAction("archive")} className="gap-1 border-[#D6E0E7] text-[#9C1E22]"><Archive size={13} /> Archive</Button>}
        </div>
      )}
    </Card>
  );
}
